/**
 * Política de privacidade (`/privacidade`). Texto puro, sem React: a tela, o
 * teste (`scripts/seo-institucional.test.mjs`) e quem mais precisar citar a
 * política leem daqui.
 *
 * Cada afirmação foi conferida no código em 2026-10-03:
 * - Meta: `api/src/auth/auth.service.ts` (cadastro), `api/src/stripe/stripe.service.ts`
 *   (assinatura), `api/src/meta-conversions/meta-conversions.service.ts` (hash SHA-256),
 *   `lib/attribution.ts` e `components/legal/cookie-consent-banner.tsx` (Pixel só com aceite).
 * - Sentry: `lib/sentry.ts` (replay só na web, 5% das sessões e as com erro, texto e campos mascarados).
 * - Vercel Analytics: `components/vercel-analytics.web.tsx`. Cloudflare: `lib/captcha/captcha-token.ts`.
 * - Endereço: `services/geocodeService.ts` (OpenStreetMap/Nominatim) e `services/cepService.ts` (ViaCEP).
 * - WhatsApp SAIR/VOLTAR: `api/src/evolution-webhook/`, `api/src/whatsapp-outbox/opt-out-keywords.ts`.
 * - Exclusão: `api/src/user/user.service.ts` (`remove`, `storageFilesOf`, `cancelStripeSubscriptions`).
 * - Banco de dados nos Estados Unidos (Supabase, região us-east-2).
 *
 * Revisão jurídica fica para depois (D7). Sem "revisado por".
 */
import { COMPANY } from '../facts';

/** Data desta versão (AAAA-MM-DD). Muda só quando o texto muda. */
export const PRIVACY_VERSION = '2026-10-03';
export const PRIVACY_VERSION_LABEL = '3 de outubro de 2026';
export const PRIVACY_PATH = '/privacidade';

export const PRIVACY_TITLE = 'Política de privacidade';
export const PRIVACY_DESCRIPTION =
  'Que dados a Controlapag coleta, para que usa, com quem compartilha, por quanto tempo guarda e como pedir acesso ou exclusão, conforme a LGPD.';

export const LEGAL_DISCLAIMER = 'Este conteúdo não é orientação jurídica.';

export const LAW_LINKS = {
  lgpd: { label: 'Lei 13.709/2018 (LGPD)', url: 'https://www.planalto.gov.br/ccivil_03/_ato2015-2018/2018/lei/l13709.htm' },
  marcoCivil: { label: 'Lei 12.965/2014 (Marco Civil da Internet)', url: 'https://www.planalto.gov.br/ccivil_03/_ato2011-2014/2014/lei/l12965.htm' },
  cdc: { label: 'Lei 8.078/1990 (Código de Defesa do Consumidor)', url: 'https://www.planalto.gov.br/ccivil_03/leis/l8078compilado.htm' },
  ecommerce: { label: 'Decreto 7.962/2013 (comércio eletrônico)', url: 'https://www.planalto.gov.br/ccivil_03/_ato2011-2014/2013/decreto/d7962.htm' },
  anpd: { label: 'ANPD (Autoridade Nacional de Proteção de Dados)', url: 'https://www.gov.br/anpd' },
} as const;

export interface LegalLink {
  label: string;
  /** URL externa (https://) ou caminho do site ("/excluir-conta"). */
  url: string;
}

export interface LegalSection {
  id: string;
  title: string;
  paragraphs: string[];
  items?: string[];
  links?: LegalLink[];
}

const city = `${COMPANY.city}/${COMPANY.region}`;

export const PRIVACY_SECTIONS: LegalSection[] = [
  {
    id: 'quem-somos',
    title: '1. Quem somos e qual é o nosso papel',
    paragraphs: [
      `A Controlapag é um app de agenda, clientes e cobrança para quem presta serviço, com sede em ${city}, Brasil. Para falar sobre seus dados, inclusive com o encarregado de dados, escreva para ${COMPANY.email}.`,
      'A Controlapag é a controladora dos dados da sua conta: nome, e-mail, telefone, login, plano e o uso que você faz do app.',
      'Já os dados que o profissional cadastra sobre os clientes dele (nome, telefone, agenda, cobranças) são do profissional. Nesses dados, o profissional é o controlador e a Controlapag é a operadora: tratamos só para o app funcionar para ele, seguindo esta política.',
    ],
  },
  {
    id: 'dados',
    title: '2. Que dados coletamos',
    paragraphs: ['Coletamos só o que o app precisa para funcionar:'],
    items: [
      'Conta: nome, e-mail, telefone (WhatsApp), senha guardada de forma embaralhada, foto de perfil e, se você entrar com Google ou Apple, o identificador e o e-mail dessas contas.',
      'Negócio (profissional): serviços, preços, agenda, endereço de atendimento, fotos da galeria, logo e capa do perfil, contratos, cobranças, despesas e plano.',
      'Clientes do profissional: nome, telefone, e-mail, agendamentos e pagamentos que o profissional cadastra ou que o cliente informa ao marcar um horário.',
      'Comprovantes: imagens de comprovante de pagamento que você anexa a uma cobrança.',
      'Assistente: o que você escreve para o assistente, só se você autorizou o uso dele.',
      'Localização: só quando você toca para preencher o endereço com a localização do aparelho. Guardamos o endereço, não o seu trajeto.',
      'Uso e técnica: endereço IP, data e hora de acesso, tipo de aparelho e navegador e registros de erro, para segurança e funcionamento.',
    ],
  },
  {
    id: 'finalidades',
    title: '3. Para que usamos e com que base legal',
    paragraphs: [
      'Cada uso tem uma base legal da LGPD (art. 7º). Não vendemos seus dados.',
    ],
    items: [
      'Criar e manter sua conta, mostrar agenda, contratos e cobranças, e mandar os lembretes que você ligou: execução do contrato com você (art. 7º, V).',
      'Processar a assinatura e o recebimento pelo link: execução do contrato (art. 7º, V) e obrigação legal, como os registros fiscais (art. 7º, II).',
      'Responder no assistente: seu consentimento (art. 7º, I), que você retira quando quiser.',
      'Medir anúncios com a Meta: seu consentimento no aviso de cookies (art. 7º, I), no site.',
      'Segurança, verificação anti-robô, registro de erros e contagem de visitas sem cookie: legítimo interesse (art. 7º, IX), sempre com o mínimo de dados.',
      'Guardar registros de acesso por 6 meses: obrigação legal do Marco Civil da Internet (art. 15).',
      'Responder a pedido de autoridade ou defender direitos em processo: obrigação legal e exercício regular de direitos (art. 7º, II e VI).',
    ],
    links: [LAW_LINKS.lgpd, LAW_LINKS.marcoCivil],
  },
  {
    id: 'ia',
    title: '4. Inteligência artificial',
    paragraphs: [
      'O assistente e a conversa de configuração na criação da conta usam um modelo de IA de terceiros. Só depois da sua permissão, enviamos à OpenRouter (openrouter.ai), que repassa ao provedor do modelo, o texto que você escreve e os dados da sua conta necessários para responder, como nomes e telefones de clientes, agenda, serviços, valores e cobranças.',
      'Você retira a permissão quando quiser, no próprio assistente. Sem ela, nada é enviado e o resto do app funciona normalmente. As mensagens do assistente podem ser apagadas a qualquer momento em "limpar conversa".',
    ],
  },
  {
    id: 'compartilhamento',
    title: '5. Com quem compartilhamos',
    paragraphs: [
      'Só com os fornecedores que fazem o app funcionar, cada um com o dado necessário para a sua parte:',
    ],
    items: [
      'Hospedagem, banco de dados, login e arquivos: Supabase e provedores de nuvem.',
      'Pagamentos: Stripe (assinatura no site e recebimento pelo link), Apple (assinatura no iPhone, pela App Store) e Google (assinatura no Android, pelo Google Play).',
      'Login social: Google e Apple, quando você escolhe entrar com eles.',
      'Mensagens: WhatsApp, pelo número da Controlapag, e e-mail transacional.',
      'Verificação anti-robô: Cloudflare, nas telas de entrar, criar conta, receber código pelo WhatsApp e esqueci a senha. A Cloudflare recebe dados técnicos do navegador, como IP e tipo de navegador.',
      'Registro de erros: Sentry. No site, gravamos a reprodução de uma pequena parte das sessões (hoje 5%) e das sessões em que acontece um erro, com todo o texto e todos os campos mascarados, para não ver o que você digita. No app, só o registro do erro. A conta aparece pelo código interno, sem nome nem e-mail.',
      'Contagem de visitas: Vercel Analytics, no site, sem cookie e sem identificar você.',
      'Endereço: OpenStreetMap (Nominatim) recebe as coordenadas quando você toca para usar a localização, ou o endereço digitado para achar o ponto no mapa. ViaCEP recebe só o CEP digitado.',
      'Inteligência artificial: OpenRouter, como na seção 4, só com sua permissão.',
      'Meta (Facebook e Instagram), só no site: veja a seção 6.',
    ],
  },
  {
    id: 'cookies',
    title: '6. Cookies e medição de anúncios',
    paragraphs: [
      'No site, usamos 3 tipos de recurso:',
      'Quando a conta de profissional é criada pelo site e existe um identificador de anúncio da Meta (o cookie do Pixel ou o código do clique que vem no link de um anúncio), enviamos à Meta o e-mail embaralhado (hash SHA-256, que não permite ler o e-mail), esses identificadores e o nome da campanha. Se essa conta assinar um plano, enviamos também o evento de assinatura com o valor do plano. Cadastros feitos pelo app (iPhone ou Android) não são enviados à Meta.',
      'Você pode apagar os cookies no seu navegador a qualquer momento. O aviso de cookies volta a aparecer depois disso.',
    ],
    items: [
      'Essenciais: mantêm você conectado, lembram suas escolhas e guardam a página por onde você chegou ao site. Sem eles, o site não funciona.',
      'Marketing (opcional): o Pixel da Meta, que só carrega se você tocar em "Aceitar" no aviso de cookies. Em "Só essenciais", ele não carrega.',
      'Medição sem cookie: a contagem de visitas da Vercel e o registro de erros do Sentry, que não usam cookie de marketing.',
    ],
  },
  {
    id: 'whatsapp',
    title: '7. Mensagens no WhatsApp',
    paragraphs: [
      'Os lembretes de horário e de cobrança saem do número da Controlapag, em nome do profissional que cadastrou o cliente. Quem não quer mais receber responde SAIR (ou PARAR) na conversa e para de receber as mensagens automáticas na hora. Para voltar a receber, basta responder VOLTAR.',
    ],
  },
  {
    id: 'transferencia',
    title: '8. Transferência internacional',
    paragraphs: [
      'O banco de dados da Controlapag fica nos Estados Unidos, e vários fornecedores da seção 5 (Stripe, Apple, Google, Meta, Sentry, Vercel, Cloudflare e OpenRouter) tratam dados fora do Brasil. Essa transferência acontece para cumprir o contrato com você e com as garantias que a LGPD pede (art. 33), como cláusulas contratuais e os compromissos de proteção de dados desses fornecedores.',
    ],
  },
  {
    id: 'retencao',
    title: '9. Por quanto tempo guardamos',
    paragraphs: [
      'Enquanto a conta existir. Depois da exclusão, só fica o que a lei manda guardar, pelo prazo da lei:',
    ],
    items: [
      'Registros de acesso (IP, data e hora): 6 meses, pelo Marco Civil da Internet (art. 15).',
      'Registros de pagamento da assinatura e documentos fiscais: 5 anos, pelo prazo da legislação tributária. Ficam também com quem processou o pagamento (Stripe, Apple ou Google), conforme as regras deles.',
      'Registros técnicos de erro: até 90 dias.',
      'Cópias de segurança do banco de dados: substituídas automaticamente em até 30 dias.',
    ],
  },
  {
    id: 'exclusao',
    title: '10. Como excluir a conta e o que é apagado',
    paragraphs: [
      'Você exclui a conta no app ou no site, em Perfil › Excluir conta. A exclusão acontece na hora. Sem acesso à conta, escreva para o e-mail de contato: confirmamos que a conta é sua e excluímos em até 15 dias.',
      'Assinatura paga com cartão pelo site é cancelada junto com a conta. Assinatura feita pela App Store ou pelo Google Play só a loja cancela: antes de excluir, cancele em Ajustes › seu nome › Assinaturas (iPhone) ou em Google Play › Pagamentos e assinaturas (Android).',
    ],
    items: [
      'Conta: nome, e-mail, telefone, senha, foto de perfil e o vínculo com o login do Google ou da Apple (o acesso pela Apple é revogado).',
      'Negócio: serviços, preços, agenda, clientes, contratos, cobranças e despesas.',
      'Arquivos: fotos da galeria, logo e capa do perfil público, fotos dos serviços e comprovantes de pagamento anexados.',
      'Perfil público e link de agendamento, que deixam de funcionar.',
      'Conversas com o assistente.',
      'A organização de que você é dono. Quem você convidou continua com a própria conta, só que desvinculado.',
    ],
    links: [{ label: 'Passo a passo da exclusão de conta', url: '/excluir-conta' }],
  },
  {
    id: 'direitos',
    title: '11. Seus direitos',
    paragraphs: [
      'Pela LGPD (art. 18), você pode pedir a qualquer momento:',
      `Peça pelo e-mail ${COMPANY.email}. Respondemos em até 15 dias. Se o dado for de um cliente cadastrado por um profissional, encaminhamos o pedido a ele, que é o controlador, e ajudamos a atender.`,
      'Se achar que não atendemos direito, você pode reclamar à ANPD, a autoridade nacional de proteção de dados.',
    ],
    items: [
      'Confirmação de que tratamos seus dados e acesso a eles.',
      'Correção de dado incompleto, errado ou desatualizado.',
      'Anonimização, bloqueio ou eliminação de dado desnecessário, excessivo ou tratado fora da lei.',
      'Portabilidade dos seus dados a outro fornecedor.',
      'Eliminação dos dados tratados com o seu consentimento, e a revogação desse consentimento (como o do assistente e o dos cookies de marketing).',
      'Informação sobre com quem compartilhamos seus dados e sobre o que acontece se você não der o consentimento.',
      'Parar de receber mensagens automáticas no WhatsApp: responda SAIR na conversa.',
    ],
    links: [LAW_LINKS.lgpd, LAW_LINKS.anpd],
  },
  {
    id: 'seguranca',
    title: '12. Segurança',
    paragraphs: [
      'Usamos conexão criptografada (HTTPS), senha guardada de forma embaralhada, acesso aos dados separado por conta e verificação anti-robô no login. Nenhum serviço na internet é livre de falha: se acontecer um incidente que possa causar risco ou dano a você, avisamos você e a ANPD, como manda a LGPD (art. 48).',
    ],
  },
  {
    id: 'menores',
    title: '13. Crianças e adolescentes',
    paragraphs: [
      'A conta da Controlapag é para maiores de 18 anos. Quando o profissional atende criança ou adolescente, como numa aula, quem fica cadastrado como cliente é o responsável, e os dados do menor são tratados só no melhor interesse dele, como pede a LGPD (art. 14).',
    ],
  },
  {
    id: 'mudancas',
    title: '14. Mudanças nesta política',
    paragraphs: [
      'Quando esta política mudar, a data no topo muda junto. Se a mudança for importante, como um novo uso dos seus dados, avisamos no app ou por e-mail antes de valer.',
    ],
  },
];
